//@flow
import React from 'react';
import {Grid, ButtonGroup, makeStyles, Typography} from '@material-ui/core';

import OrderCreatedFilter from './order-created-filter';
import OrderAmountFilter from './order-amount-filter';
import OrderItemCategoryFilter from './order-item-category-filter';
import CrmCustomerCategoryFilter from './crm-customer-category-filter';

import type {
  FilterType,
  CrmSegmentFilter,
  CrmSegmentFilterConditions,
} from '../../../../../../entities/crm/segment';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';

export type FilterRowProps = {
  conditions: CrmSegmentFilterConditions,
  onChange: (conditions: CrmSegmentFilterConditions) => void,
  onDelete: () => void,
};

type Props = {
  filter: CrmSegmentFilter,
  onChange: (filter: CrmSegmentFilter) => void,
  disabled?: boolean,
};

const FILTER_COMPONENT_MAP = {
  ORDER_CREATED: OrderCreatedFilter,
  ORDER_AMOUNT: OrderAmountFilter,
  ORDER_ITEM_CATEGORY: OrderItemCategoryFilter,
  CRM_CUSTOMER_CATEGORY: CrmCustomerCategoryFilter,
};

const useStyles = makeStyles(theme => ({
  root: {
    '&[data-disabled="true"]': {
      pointerEvents: 'none',
      opacity: 0.85,
    },
  },
  divider: {
    margin: '1rem 0',
  },
  subFilterIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
  },
  addFilterContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '1rem',
    gap: '1rem',
    border: '2px dashed #B8B8C7',
  },
}));

export default function SegmentFilters(props: Props) {
  const {filter, onChange, disabled} = props;

  const classes = useStyles();

  const MainFilterComponent = FILTER_COMPONENT_MAP[filter.filterType];

  const handleFilterChange = (conditions: CrmSegmentFilterConditions) => {
    onChange({
      ...filter,
      conditions,
    });
  };

  const handleDelete = () => {
    // THE MAIN FILTER CANNOT BE DELETED
    console.log('delete');
  };

  const handleSubFilterChange = (subFilter: CrmSegmentFilter) => {
    const newSubFilters = filter.subFilters?.map(f => {
      if (f.filterType === subFilter.filterType) {
        return subFilter;
      }

      return f;
    });
    onChange({
      ...filter,
      subFilters: newSubFilters,
    });
  };

  const handleSubFilterDelete = (subFilter: CrmSegmentFilter) => {
    const newSubFilters = filter.subFilters?.filter(
      f => f.filterType !== subFilter.filterType
    );
    onChange({
      ...filter,
      subFilters: newSubFilters,
    });
  };

  const handleAddFilter = (filterType: FilterType) => {
    const newSubFilter: CrmSegmentFilter = {
      filterType,
      conditions: {},
    };

    const subFilters = filter.subFilters ?? [];

    onChange({
      ...filter,
      subFilters: [...subFilters, newSubFilter],
    });
  };

  return (
    <Grid
      container
      spacing={2}
      className={classes.root}
      data-disabled={disabled}
    >
      <Grid xs={12} item>
        <MainFilterComponent
          conditions={filter.conditions}
          onChange={handleFilterChange}
          onDelete={handleDelete}
        />
      </Grid>

      {filter.subFilters?.map((subFilter, index) => {
        const FilterComponent = FILTER_COMPONENT_MAP[subFilter.filterType];

        const handleFilterChange = (conditions: CrmSegmentFilterConditions) => {
          const newSubFilter = {
            ...subFilter,
            conditions,
          };

          handleSubFilterChange(newSubFilter);
        };

        const handleDelete = () => {
          handleSubFilterDelete(subFilter);
        };

        return (
          <>
            <Grid
              key={index + 'svg'}
              xs={1}
              item
              className={classes.subFilterIcon}
            >
              <svg
                width="22"
                height="27"
                viewBox="0 0 22 27"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.3333 24C16.3333 25.4728 17.5272 26.6667 19 26.6667C20.4728 26.6667 21.6667 25.4728 21.6667 24C21.6667 22.5272 20.4728 21.3333 19 21.3333C17.5272 21.3333 16.3333 22.5272 16.3333 24ZM0.5 0V20H1.5V0H0.5ZM5 24.5H19V23.5H5V24.5ZM0.5 20C0.5 22.4853 2.51472 24.5 5 24.5V23.5C3.067 23.5 1.5 21.933 1.5 20H0.5Z"
                  fill="#B8B8C7"
                />
              </svg>
            </Grid>
            <Grid
              key={index}
              xs={11}
              item
              className={classes.subFilterContainer}
            >
              <FilterComponent
                conditions={subFilter.conditions}
                onChange={handleFilterChange}
                onDelete={handleDelete}
              />
            </Grid>
          </>
        );
      })}

      <Grid xs={12} item>
        <Typography align="center" variant="body1">
          Agregar filtros
        </Typography>
      </Grid>
      <Grid xs={11} item className={classes.addFilterContainer}>
        <ButtonGroup fullWidth variant="text" className={classes.buttonGroup}>
          <CustomButton
            color="primary"
            onClick={() => handleAddFilter('ORDER_AMOUNT')}
            disabled={filter.subFilters?.some(
              f => f.filterType === 'ORDER_AMOUNT'
            )}
          >
            Monto de compra
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={() => handleAddFilter('ORDER_ITEM_CATEGORY')}
            disabled={filter.subFilters?.some(
              f => f.filterType === 'ORDER_ITEM_CATEGORY'
            )}
          >
            Categoría de productos
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={() => handleAddFilter('CRM_CUSTOMER_CATEGORY')}
            disabled={filter.subFilters?.some(
              f => f.filterType === 'CRM_CUSTOMER_CATEGORY'
            )}
          >
            Categoría de clientes
          </CustomButton>
        </ButtonGroup>
      </Grid>
    </Grid>
  );
}
